import './styles/SignIn.scss';

import { AuthLayout } from '@whistler-ui/k2/components/AuthLayout';
import { Button } from '@whistler-ui/k2/components/Button';
import { Form } from '@whistler-ui/k2/components/Form';
import { InputField } from '@whistler-ui/k2/components/InputField';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Heading, Paragraph } from '@whistler-ui/k2/components/Typography';
import { LOGIN_MSG } from '@whistler-ui/k2/constants';
import {
  isBase64,
  isFieldError,
  removeQuotesFromText,
} from '@whistler-ui/k2/helpers';
import { getUserFacingErrorMessage } from '@whistler-ui/k2/helpers/userFacingError';
import {
  useAppMessages,
  useClassHelper,
  useQuery,
} from '@whistler-ui/k2/hooks';
import { useFormik } from 'formik';
import { decode } from 'js-base64';
import { MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import * as Yup from 'yup';

import { NetworkRestrictionAlert } from '../../../components';
import { commonPageStylesIdentifier } from '../../../constants';
import {
  useAsyncGetOrganisationDetailsById,
  useGetCurrentUser,
  useRenderIf,
  useSignInCognitoUser,
  useStoredPreviousUrl,
} from '../../../hooks';
import { routes } from '../../../routes';
import { isInvalidOrganisation } from '../../../routes/helpers';
import { inAuthWidget } from '../../../routes/Root';
import { widgetAuthentication } from '../../../routes/Root';
import { getCognitoUser, signOut } from '../../../services';
import { useAuthStore } from '../../../store';
import { useThemeStore } from '../../../store/theme';
import { isOrganisation } from '../../../types';

const componentName = 'c-SignIn';

const SignIn = () => {
  const { getSearchParams } = useQuery();
  const email = getSearchParams('email');
  const tempPass = getSearchParams('temp_pass');

  const c = useClassHelper(componentName);
  const commonC = useClassHelper(commonPageStylesIdentifier);
  const navigate = useNavigate();
  const { previousUrlValue, clearPreviousUrl } = useStoredPreviousUrl();
  const { t } = useTranslation();
  const { config } = useThemeStore();
  const { isGSMA } = useRenderIf();

  const [setMessages] = useAppMessages(t);

  const { mutateAsync: signInCognitoUser } = useSignInCognitoUser();
  const { mutateAsync: getCurrentUser } = useGetCurrentUser();

  const getOrganisationDetails = useAsyncGetOrganisationDetailsById();

  const { setAuthValues, resetStore, setCognitoValues } = useAuthStore();

  const clearAuth = async () => {
    resetStore();

    // If something wrong happens (after signCognitoUser, maybe getting getCurrentUser)
    // i need to force sign out
    await signOut({ forceSignOut: true });
  };

  const initialValues = useMemo(() => {
    const isValidEmail64 = isBase64(email);
    const isValidTempPw64 = isBase64(tempPass);

    const decodedEmail = isValidEmail64 ? decode(email) : '';
    const decodedTempPassword = isValidTempPw64 ? decode(tempPass) : '';

    return {
      email: decodedEmail,
      password: decodedTempPassword,
    };
  }, [email, tempPass]);

  const formik = useFormik({
    initialValues,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Clearing the authentication session and auth store to ensure all authentication-related keys and tokens are removed.
        await clearAuth();

        const cognitoResult = await signInCognitoUser({
          email: values.email,
          password: values.password,
        });

        if (cognitoResult) {
          setCognitoValues(cognitoResult);
        }

        // v6's `signIn()` resolves `{isSignedIn, nextStep: {signInStep}}` —
        // there is no `.challengeName` (that was a v5 `CognitoUser` shape).
        // Route off `nextStep.signInStep` instead (verified against the
        // installed `aws-amplify` v6 source).
        switch (cognitoResult.nextStep?.signInStep) {
          case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED': {
            navigate(routes.changePassword.url, {
              state: { email: values.email },
            });
            break;
          }
          case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
            navigate(routes.mfaSetup.url, {
              state: { email: values.email },
            });
            break;
          }
          case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE': {
            navigate(routes.totp.url);
            break;
          }
          default: {
            const loggedUser = await getCurrentUser({ silent: false });
            const cognitoUser = await getCognitoUser({ silent: false });
            const organisationId = loggedUser.organisation_id;

            const organisation = await getOrganisationDetails.mutateAsync({
              organisation_id: organisationId,
              silent: true, // This line prevent to throw an error if the organisation have an error code (403|404|401)
            });

            const haveOrganisation = isOrganisation(organisation);

            // Forcing SignOut if the user is part of Suspended organisation
            if (haveOrganisation) {
              if (organisation.status === 'Suspended') {
                // Focing logout
                await clearAuth();
                setMessages(LOGIN_MSG.SUSPENDED_USER);
                return;
              }
            } else {
              // If user doesnt have any organisation and is not an internal support.
              if (typeof loggedUser.internal_support_user === 'undefined') {
                // Focing logout
                await clearAuth();
                setMessages({
                  type: useAppMessages.type.ERROR,
                  msg: 'No organisation founded, please try again.',
                });
                return;
              }
            }

            const authenticatedValues = {
              cognito: cognitoUser,
              loggedUser,
              ...(haveOrganisation && {
                organisationState: {
                  organisation,
                },
              }),
            };

            setAuthValues(authenticatedValues);

            // If user is internal_support it will be redirected to dashboard or previously visited url
            // This logic needs to be here because i need to fill AuthValues
            if (!haveOrganisation && 'internal_support_user' in loggedUser) {
              const prevPushUrl = removeQuotesFromText(previousUrlValue);
              navigate(prevPushUrl || routes.dashboard.url);
              return;
            }

            // If the organisation is invalid, we redirect to under review page
            const inactiveStatus = isInvalidOrganisation(organisation.status);

            if (inactiveStatus && !inAuthWidget) {
              navigate(routes.underReview.url, { replace: true });
              return;
            }

            // If the user is not part of an inactive organisation, we can proceed to the dashboard
            const prevPushUrl = removeQuotesFromText(previousUrlValue);

            if (inAuthWidget) {
              return widgetAuthentication(cognitoUser.username);
            } else {
              navigate(prevPushUrl || routes.dashboard.url);
            }

            if (previousUrlValue) {
              clearPreviousUrl();
            }

            break;
          }
        }
      } catch (error) {
        await clearAuth();

        setMessages({
          type: useAppMessages.type.ERROR,
          msg: getUserFacingErrorMessage(error, 'common:errors.default'),
        });
      } finally {
        setSubmitting(false);
      }
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email('loginPage_form_error_email_is_not_valid')
        .required('loginPage_form_error_email_is_required'),
      password: Yup.string().required('loginPage_form_error_password_required'),
    }),
  });

  const {
    errors,
    handleBlur,
    handleChange,
    isValid,
    touched,
    values,
    handleSubmit,
    isSubmitting,
  } = formik;

  const disabledLinkOnLoading = (e: MouseEvent<HTMLAnchorElement>): void => {
    if (isSubmitting) {
      e.preventDefault();
    }
  };

  const isSubmitBtnDisabled = !isValid || isSubmitting;

  return (
    <AuthLayout
      logoUrl={config.assets.logo}
      config={config}
      imprintUrl={config.environments.settings.imprintUrl}
      className={inAuthWidget ? 'flex items-center' : ''}
    >
      <div className={componentName}>
        <Stack direction="column" gap={6}>
          <Heading>{t('loginPage_title_log_in')}</Heading>
          <Form onSubmit={handleSubmit} formClassName={c('__form')}>
            <Stack direction="column" gap={3.5}>
              <InputField
                label={t('loginPage_form_work_email')}
                id="email"
                error={isFieldError('email', touched, errors)}
                errorMessage={errors.email ? t(errors.email) : undefined}
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder={t('loginPage_form_placeholder_work_email')}
                touched={touched.email?.toString()}
                type="text"
                autoComplete="off"
                value={values.email}
                disabled={isSubmitting}
                aria-disabled={isSubmitting}
                data-cy="email"
              />

              <InputField
                label={t('loginPage_form_password')}
                id="password"
                error={isFieldError('password', touched, errors)}
                errorMessage={errors.password ? t(errors.password) : undefined}
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder={t('loginPage_form_placeholder_password')}
                touched={touched.password?.toString()}
                type="password"
                autoComplete="off"
                value={values.password}
                disabled={isSubmitting}
                aria-disabled={isSubmitting}
                data-cy="password"
              />
              <div className={c('__button-link')}>
                <div className={c('__page-link')}>
                  <div className={commonC('__link')}>
                    <Link
                      to={routes.forgotPassword.url}
                      onClick={disabledLinkOnLoading}
                      aria-disabled={isSubmitting}
                    >
                      {t('loginPage_link_forgot_password')}
                    </Link>
                  </div>
                </div>
                <div className={c('__button')}>
                  <Button
                    size="lg"
                    type="submit"
                    id={c('__button')}
                    disabled={isSubmitBtnDisabled}
                    loading={isSubmitting}
                    data-cy="sign-in-submit"
                  >
                    {t('loginPage_form_login')}
                  </Button>
                </div>
              </div>
            </Stack>
            {isGSMA && (
              <Paragraph className={c('__terms_of_use')}>
                {t('signupPageUserDetails_form_checkbox_agree_GSMA')}{' '}
                <a
                  href="https://www.gsmaservices.com/network-services/entitlements/gsma-entitlements-terms-of-use/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>
                .
              </Paragraph>
            )}
          </Form>
          <footer>
            <Paragraph>
              {t('loginPage_link_dont_have')}
              &nbsp;
              <Link
                to={routes.signup.url}
                onClick={disabledLinkOnLoading}
                aria-disabled={isSubmitting}
              >
                {t('loginPage_link_signup')}
              </Link>
            </Paragraph>
          </footer>
          <NetworkRestrictionAlert />
        </Stack>
      </div>
    </AuthLayout>
  );
};

export default SignIn;
